import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import toast from 'react-hot-toast'
import {
  SchedulerKitchenCycleRowType,
  SchedulerPackagingRoutesRowType,
} from './Scheduler.types'

type BaseOption = { code: string; name: string }

type Props = {
  /** The saved `KitchenCyclesDelta` rows. */
  cycleRows: SchedulerKitchenCycleRowType[]
  /** Packing routes: where the kitchens and their bases come from. */
  routes: unknown[]
  /** Declared tanks, so a tank named in the Kitchen column is not offered. */
  tanks: unknown[]
  onSave: (rows: Record<number, SchedulerKitchenCycleRowType>) => void
  onDelete: (rows: SchedulerKitchenCycleRowType[]) => Promise<unknown>
  isBusy: boolean
}

/**
 * Kitchen cycles, authored per kitchen: pick the kitchen, then number the
 * bases it makes in the order the cycle should run them. Save writes one
 * `KitchenCyclesDelta` row per numbered base and removes the rows of any base
 * on that kitchen left blank, so the table is always the result of this
 * popup rather than something typed row by row.
 *
 * Storage-agnostic: the per-schedule Raw Data view and the SOR page each pass
 * their own rows and save/delete calls.
 */
export const KitchenCycleEditor = ({
  cycleRows,
  routes,
  tanks,
  onSave,
  onDelete,
  isBusy,
}: Props) => {
  // Kitchen -> the base materials a packing route makes on it. A holding
  // tank can be named in the Kitchen column (v7.152.0), and a tank is not a
  // kitchen a cycle can run on, so declared tanks are left out.
  const basesByKitchen = useMemo(() => {
    const tankIds = new Set(
      (tanks as Array<{ tankId?: string }>)
        .map((t) => String(t.tankId ?? '').trim())
        .filter(Boolean)
    )
    const out = new Map<string, Map<string, string>>()
    for (const r of routes as SchedulerPackagingRoutesRowType[]) {
      const kitchen = String(r.kitchen ?? '').trim()
      const code = String(r.baseProductCode ?? '').trim()
      if (!kitchen || !code || tankIds.has(kitchen)) continue
      if (!out.has(kitchen)) out.set(kitchen, new Map())
      out.get(kitchen)!.set(code, String(r.baseProductName ?? ''))
    }
    return out
  }, [routes, tanks])
  const kitchens = useMemo(
    () => Array.from(basesByKitchen.keys()).sort(),
    [basesByKitchen]
  )

  const [kitchen, setKitchen] = useState('')
  const [open, setOpen] = useState(false)
  // base code -> the position typed for it ('' = not in the cycle)
  const [positions, setPositions] = useState<Record<string, string>>({})

  const bases: BaseOption[] = useMemo(
    () =>
      Array.from(basesByKitchen.get(kitchen)?.entries() ?? [])
        .map(([code, name]) => ({ code, name }))
        .sort((a, b) => a.code.localeCompare(b.code)),
    [basesByKitchen, kitchen]
  )
  // Where each base currently sits in a cycle, on any kitchen.
  const savedByBase = useMemo(() => {
    const m = new Map<string, SchedulerKitchenCycleRowType>()
    for (const r of cycleRows) m.set(String(r.baseMaterialCode).trim(), r)
    return m
  }, [cycleRows])

  const openPopup = () => {
    const initial: Record<string, string> = {}
    for (const b of bases) {
      const saved = savedByBase.get(b.code)
      initial[b.code] =
        saved && String(saved.kitchen).trim() === kitchen
          ? String(saved.position ?? '')
          : ''
    }
    setPositions(initial)
    setOpen(true)
  }

  const typed = Object.entries(positions).filter(([, v]) => v.trim() !== '')
  const invalid = typed.filter(([, v]) => !/^[1-9]\d*$/.test(v.trim()))
  const counts = new Map<string, number>()
  for (const [, v] of typed)
    counts.set(v.trim(), (counts.get(v.trim()) ?? 0) + 1)
  const duplicates = Array.from(counts.entries())
    .filter(([, n]) => n > 1)
    .map(([p]) => p)
  const canSave = invalid.length === 0 && duplicates.length === 0

  const save = async () => {
    // Numbered bases: upsert (the base keys the row, so a base that was in
    // another kitchen's cycle moves to this one).
    const upserts: Record<number, SchedulerKitchenCycleRowType> = {}
    typed.forEach(([code, v], i) => {
      upserts[i + 1] = {
        id: i + 1,
        kitchen,
        baseMaterialCode: code,
        position: Number(v.trim()),
      }
    })
    // Bases of this kitchen left blank that were in its cycle: remove.
    const removals = cycleRows.filter(
      (r) =>
        String(r.kitchen).trim() === kitchen &&
        (positions[String(r.baseMaterialCode).trim()] ?? '').trim() === ''
    )
    try {
      if (removals.length > 0) await onDelete(removals)
      if (typed.length > 0) onSave(upserts)
      if (typed.length === 0 && removals.length === 0) {
        toast('Nothing to save')
      }
      setOpen(false)
    } catch {
      toast.error('Failed to save the cycle')
    }
  }

  const movedFrom = (code: string) => {
    const saved = savedByBase.get(code)
    const k = saved ? String(saved.kitchen).trim() : ''
    return k && k !== kitchen ? k : ''
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each base material in a cycle is made only on its kitchen, each
        week&apos;s demand for it is its own batch, and the kitchen runs them in
        position order: week 1&apos;s cycle, then week 2&apos;s, and so on.
        Other base materials may run on the kitchen in between.
      </Typography>
      <Box display="flex" gap={2} alignItems="center" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="kitchen-cycle-kitchen">Kitchen</InputLabel>
          <Select
            labelId="kitchen-cycle-kitchen"
            label="Kitchen"
            value={kitchen}
            onChange={(e) => setKitchen(String(e.target.value))}
          >
            {kitchens.map((k) => (
              <MenuItem key={k} value={k}>
                {k}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" disabled={!kitchen} onClick={openPopup}>
          Set cycle
        </Button>
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Cycle for {kitchen}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Number the base materials in the order the cycle runs them (1, 2,
            3…). Leave a base blank to keep it out of the cycle.
          </Typography>
          {bases.length === 0 ? (
            <Alert severity="info">
              No packing route makes a base material on {kitchen}.
            </Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Base Material Code</TableCell>
                  <TableCell>Base Material</TableCell>
                  <TableCell width={140}>Position in Cycle</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bases.map((b) => {
                  const v = positions[b.code] ?? ''
                  const bad =
                    v.trim() !== '' &&
                    (!/^[1-9]\d*$/.test(v.trim()) ||
                      duplicates.includes(v.trim()))
                  const from = movedFrom(b.code)
                  return (
                    <TableRow key={b.code}>
                      <TableCell>{b.code}</TableCell>
                      <TableCell>
                        {b.name}
                        {from && (
                          <Typography
                            variant="caption"
                            color="warning.main"
                            display="block"
                          >
                            In {from}&apos;s cycle; a number here moves it to{' '}
                            {kitchen}.
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={v}
                          error={bad}
                          inputProps={{ inputMode: 'numeric' }}
                          onChange={(e) =>
                            setPositions({
                              ...positions,
                              [b.code]: e.target.value,
                            })
                          }
                        />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
          {invalid.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Positions must be whole numbers from 1.
            </Alert>
          )}
          {duplicates.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Two bases share position {duplicates.join(', ')}. Give each base
              its own position.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={!canSave || isBusy}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
